import { LOCALE_LISTING_PATHS } from 'types/locales';

/**
 * Enum of all possible listing section IDs.
 * 
 * Provides type-safe identifiers for listing sections to prevent typos
 * and enable better IDE autocomplete.
 */
export enum ListingSectionId {
  OVERVIEW = 'overview',
  PRICE_HISTORY = 'price-history',
  HOME_APPRAISAL = 'home-appraisal',
  MAP = 'map',
  SIMILAR_LISTINGS = 'similar-listings'
}

/**
 * Configuration for a listing page section that can be navigated to.
 * 
 * Represents the information architecture of a listing page - which sections exist,
 * their order, and when they should be shown based on data availability.
 * 
 * This is domain logic: "What sections does a listing have?"
 * NOT UI logic: "How should we navigate to them?"
 */
export interface ListingSectionConfig {
  /** Unique identifier for the section */
  id: ListingSectionId;
  
  /** Translation key from LOCALE_LISTING_PATHS for the section name */
  labelKey: string;
  
  /** Icon identifier (UI layer maps this to actual components) */
  iconName: string;
  
  /** Whether this section requires data to be shown */
  isConditional?: boolean;
  
  /** Key in SectionVisibilityFlags that determines if this section has data */
  visibilityKey?: keyof SectionVisibilityFlags;
}

/**
 * Flags indicating which content sections have available data for a listing.
 * 
 * Domain logic: "Does this listing have price history? Similar listings? etc."
 */
export interface SectionVisibilityFlags {
  /** True if price history data is available for this listing */
  shouldShowPriceHistory: boolean;
  
  /** True if home appraisal is available for this listing */
  shouldShowAppraisal: boolean;
  
  /** True if similar listings are available */
  shouldShowSimilarListings: boolean;
  
  /** True if commute/transportation data is available */
  shouldShowCommute: boolean;

  /** True if the listing map section/tab should be shown (suppressed for hidden-address listings). */
  shouldShowMap: boolean;
}

/**
 * Default section structure for listing pages.
 * 
 * Defines the canonical order and availability of listing content sections.
 * This represents the information architecture - what information exists for a listing.
 * 
 * **Order matters:** Sections are presented in this order.
 * 
 * @example
 * ```ts
 * // To add a new section:
 * {
 *   id: 'schools',
 *   labelKey: LOCALE_LISTING_PATHS.SECTION_SCHOOLS,
 *   iconName: 'schools',
 *   isConditional: true,
 *   visibilityKey: 'shouldShowSchools',
 * }
 * ```
 */
export const DEFAULT_LISTING_SECTIONS_CONFIG: ListingSectionConfig[] = [
  {
    id: ListingSectionId.OVERVIEW,
    labelKey: LOCALE_LISTING_PATHS.TAB_OVERVIEW,
    iconName: 'overview',
    isConditional: false, // Every listing has an overview
  },
  {
    id: ListingSectionId.PRICE_HISTORY,
    labelKey: LOCALE_LISTING_PATHS.TAB_PRICE_HISTORY,
    iconName: 'price-history',
    isConditional: true,
    visibilityKey: 'shouldShowPriceHistory',
  },
  {
    id: ListingSectionId.HOME_APPRAISAL,
    labelKey: LOCALE_LISTING_PATHS.TAB_HOME_APPRAISAL,
    iconName: 'home-appraisal',
    isConditional: true,
    visibilityKey: 'shouldShowAppraisal',
  },
  {
    id: ListingSectionId.MAP,
    labelKey: LOCALE_LISTING_PATHS.TAB_MAP,
    iconName: 'map',
    isConditional: true,
    visibilityKey: 'shouldShowMap',
  },
  {
    id: ListingSectionId.SIMILAR_LISTINGS,
    labelKey: LOCALE_LISTING_PATHS.TAB_SIMILAR_LISTINGS,
    iconName: 'similar-listings',
    isConditional: true,
    visibilityKey: 'shouldShowSimilarListings',
  },
];
